<script lang="ts" setup>
// Generated with AI

import { ref } from "vue"
import { route } from "@/common/utils/ziggy"
import axios from "axios"

type PendingRegistration = {
  id: number
  email: string
  name: string
  type: string
  domain: string
  submitted: string
}

const props = defineProps<{
  pending: PendingRegistration[]
  emailConfirmationEnabled: boolean
}>()

const pendingList = ref<PendingRegistration[]>([...props.pending])
const processingIds = ref<number[]>([])
const featureEnabled = ref(props.emailConfirmationEnabled)
const toggling = ref(false)

async function toggleFeature() {
  toggling.value = true
  try {
    const newValue = !featureEnabled.value
    await axios.post(route("admin.settings.set"), {
      key: "features.email_confirmation",
      value: newValue,
    })
    featureEnabled.value = newValue
  } catch (error) {
    console.error("Failed to toggle feature:", error)
  } finally {
    toggling.value = false
  }
}

async function approve(item: PendingRegistration) {
  // if (!confirm(`Approve registration for ${item.email}?`)) return

  processingIds.value = [...processingIds.value, item.id]
  try {
    await axios.post(route("admin.registration-approvals.approve", { id: item.id }))
    pendingList.value = pendingList.value.filter((r) => r.id !== item.id)
  } catch (error) {
    console.error("Failed to approve:", error)
  } finally {
    processingIds.value = processingIds.value.filter((id) => id !== item.id)
  }
}

async function deny(item: PendingRegistration) {
  // if (!confirm(`Deny registration for ${item.email}?`)) return

  processingIds.value = [...processingIds.value, item.id]
  try {
    await axios.post(route("admin.registration-approvals.deny", { id: item.id }))
    pendingList.value = pendingList.value.filter((r) => r.id !== item.id)
  } catch (error) {
    console.error("Failed to deny:", error)
  } finally {
    processingIds.value = processingIds.value.filter((id) => id !== item.id)
  }
}
</script>

<template>
  <div class="container-fluid">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h1>Registration Approvals</h1>
      <a :href="route('admin.registration-approvals.patterns')" class="btn btn-outline-primary">
        Edit Patterns
      </a>
    </div>

    <div class="card mb-4">
      <div class="card-body d-flex align-items-center">
        <b-form-checkbox
          switch
          :checked="featureEnabled"
          :disabled="toggling"
          @change="toggleFeature"
        >
          Email confirmation <strong>{{ featureEnabled ? 'enabled' : 'disabled' }}</strong>
        </b-form-checkbox>
      </div>
    </div>

    <div v-if="pendingList.length === 0" class="alert alert-info">
      No pending registrations.  Only pending registrations for email addresses matching the list of
      <a :href="route('admin.registration-approvals.patterns')">patterns requiring admin approval</a>
      will show up here -- all other registrations are automatic once confirmed by the user.
    </div>

    <table v-else class="table table-striped">
      <thead>
        <tr>
          <th>Email</th>
          <th>Name</th>
          <th>Type</th>
          <th>Domain</th>
          <th>Submitted</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="item in pendingList" :key="item.id">
          <td>{{ item.email }}</td>
          <td>{{ item.name }}</td>
          <td>{{ item.type }}</td>
          <td>{{ item.domain }}</td>
          <td>{{ item.submitted }}</td>
          <td>
            <button
              class="btn btn-sm btn-success me-1"
              :disabled="processingIds.includes(item.id)"
              @click="approve(item)"
            >
              Approve
            </button>
            <button
              class="btn btn-sm btn-danger"
              :disabled="processingIds.includes(item.id)"
              @click="deny(item)"
            >
              Deny
            </button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
